import { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { CommonContext, ProgressContext, UserContext } from '../../contexts';
import { getConvertedByteSize, makeUrl, removeWhitespace } from '../../utils/common';

import queryString from 'query-string';
import toast from 'react-hot-toast';
import { editPost, getFilesByPostId, getPost } from '../../data/mysql';
import BoardHeader from './BoardHeader';

import SunEditor from 'suneditor-react';
import { DataTypes } from '../../data/Types';

const g_filesToBeDeleted = new Array();

const BoardEdit = () => {
  const { spinner } = useContext(ProgressContext);
  const { settings } = useContext(CommonContext);
  const { categoryId } = useParams();
  const { user } = useContext(UserContext);

  const inputRef = useRef();
  const textareaRef = useRef();

  const { search } = useLocation();
  const { id, searching, keyword, offset, item } = queryString.parse(search);

  const navigate = useNavigate();

  const [files, setFiles] = useState([{}, {}, {}, {}, {}]); // 5 Files upload

  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');

  const [department, setDepartment] = useState(''); // 관련부서
  const [departments, setDepartments] = useState([]); // 부서 목록: 총무부|목회부|재무부|교육부|청소년부|어린이부|디지털선교부 ...

  const [isSendable, setIsSendable] = useState(false);

  // 0. 마운트 할 때
  useEffect(() => {
    inputRef.current.focus(); // 보통은 글의 내용을 수정을 먼저하지 않을까?

    const init = async () => {
      const post = await getPost({ postId: id, categoryId });
      const uploads = await getFilesByPostId({ postId: id });

      setName(post.name);
      setDepartment(post.department);
      setTitle(post.title);
      setContent(post.content);
      setFiles(files.map((file, index) => ({ ...file, ...uploads[index] })));
    };
    init();
  }, []);

  /* [저장] 버튼을 활성화 시킨다 */
  useEffect(() => {
    setIsSendable(removeWhitespace(title) && removeWhitespace(content));
  }, [title, content]);

  /* [Admin] 셋팅값에서 관리자 부서의 종류를 가지고 온다. */
  useEffect(() => {
    const result = settings?.find((setting) => setting.name === 'admin_department')?.value;

    if (result) {
      setDepartments(result.split('|'));
    }
  }, [settings]);

  /**
   * 해당 글을 수정한다.
   */
  const _handleSubmitButtonPress = async () => {
    try {
      spinner.start();

      // 지워질 파일과 업로드할 파일이 무엇인지 출력해보자
      // console.log(categoryId, id, title, content, files.filter(file => file.name) );
      const result = await editPost({ department, categoryId, postId: id, title, content, filesToDelete: g_filesToBeDeleted, files: files.filter((file) => file.name) });

      navigate(searching ? makeUrl('../view', { searching, item, keyword, offset, id }) : makeUrl('../view', { offset, id })); // 추후 해당 글을 바로 볼 수 있도록 수정하던지... 안하던지...

      if (result) {
        toast.success('수정 되었습니다.');
      } else {
        toast.error('글 수정이 실패되었습니다.');
      }
    } catch (e) {
      console.log(e.code, e.message);
    } finally {
      spinner.stop();
    }
  };

  /**
   * 파일을 올린다.
   * @param {*} param0
   */
  const _handleFileChange = ({ fileIndex, uploadFile }) => {
    if (uploadFile.size > 30 * 1024 * 1024) {
      // 파일의 크기는 30M 이하이어아 한다.
      toast.error('30MB를 초과하는 파일은 업로드할 수 없습니다');
    } else {
      setFiles(files.map((file, index) => (index === fileIndex ? uploadFile : file)));
    }
  };

  /**
   * 선택된 파일를 파일 목록에서 제거하여 해당 항목을 비운다.
   * : 파일을 취소하고 최종적으로 [수정]을 클릭하여야 서버에 있는 파일과 DB정보를 삭제한다.
   * @param {*} param0
   */
  const _handleCanceledFileUpload = ({ fileId, fileIndex }) => {
    g_filesToBeDeleted.push(fileId); // 삭제할 파일(id) 리스트를 만든다.

    setFiles(files.map((file, index) => (index === fileIndex ? {} : file)));
  };

  /**
   * 웹 에디터(SunEditor) 글을 작성할 때
   */
  const _handleChangeContent = (content) => {
    setContent(content);
  };

  /**
   * 관리자 기능: 해당 부서로 작성자를 표시한다.
   */
  const _handleChangeDepartment = (value) => {
    if (value !== 'default') {
      setDepartment(value);
    }
  };

  return (
    <div id="primary" className="content-area">
      {/* <!--s:#content --> */}
      <div id="content" className="site-content">
        <article>
          <div>
            <BoardHeader />
            <section>
              <div className="table-write">
                <input type="hidden" name="return_url" id="return_url" value="" />
                <table>
                  <tbody>
                    <tr>
                      <th>
                        제목<span className="rs_fc_red">*</span>
                      </th>
                      <td>
                        <div className="col-sm-10">
                          <input
                            ref={inputRef}
                            type="text"
                            className="form-control"
                            name="bc_subject"
                            id="bc_subject"
                            value={title}
                            onChange={({ target }) => setTitle(target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                textareaRef.current.focus();
                                e.preventDefault();
                              }
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                    {user && user.level >= DataTypes.USER_LEVEL_STAFF && (
                      <tr>
                        <th>관련부서</th>
                        <td>
                          <label>
                            <select className="form-control" onChange={(e) => _handleChangeDepartment(e.target.value)} key={department} defaultValue={department}>
                              <option key={0} value="default">
                                {name}
                              </option>
                              {departments?.map((name, idx) => (
                                <option key={idx + 1} value={name}>
                                  {name}
                                </option>
                              ))}
                            </select>
                          </label>
                        </td>
                      </tr>
                    )}

                    {
                      /* 로그인을 하고, 사용자 Level이 운영진 이상일 경우 공지를 할 수 있다. */
                      // user && user.level >= DataTypes.USER_LEVEL_STAFF && (
                      //     <tr>
                      //         <th>옵션</th>
                      //         <td>
                      //             <div className='col-sm-10'>
                      //                 <label className='checkbox-inline'>
                      //                     <input type='checkbox' value='notice' onChange={({ target }) => setOption(target.value)} />
                      //                     공지글
                      //                 </label>
                      //             </div>
                      //         </td>
                      //     </tr>
                      // )
                    }

                    <tr>
                      <th>
                        내용 <span className="rs_bold rs_fc_red">* </span>
                      </th>
                      <td>
                        <div className="col-sm-12">
                          <SunEditor
                            ref={textareaRef}
                            lang="ko"
                            height="340"
                            setOptions={{
                              height: 200,
                              font: ['명조체', '궁서체', '굴림체', '견고딕', '가는고딕', '나눔고딕'],
                              buttonList: [
                                ['undo', 'redo', 'font', 'fontSize', 'formatBlock'],
                                ['bold', 'underline', 'italic', 'strike'],
                                '/', // Line break
                                ['fontColor', 'hiliteColor', 'outdent', 'indent', 'align', 'horizontalRule', 'table'],
                                ['link', 'image', 'video', 'fullScreen', 'preview', 'print'],
                              ],
                              fontSize: [8, 9, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72],
                            }}
                            setContents={content}
                            onChange={_handleChangeContent}
                          />
                        </div>
                      </td>
                    </tr>

                    {
                      /* [Files 출력부] ====================================================== */
                      files.map((file, index) => {
                        return !file.name ? (
                          <tr key={index}>
                            <th>파일 #{index + 1}</th>
                            <td>
                              <div className="col-sm-10">
                                <input
                                  type="file"
                                  id="files"
                                  className="form-control upfile"
                                  // title={file.name ? `${file.name} (${getConvertedByteSize(file.size)})` : `첨부파일 MAX SIZE(30M) 입니다.`}
                                  title={`${file.name} (${getConvertedByteSize(file.size)})`}
                                  onChange={(e) => _handleFileChange({ fileIndex: index, uploadFile: e.target.files[0] })}
                                />
                                <span></span>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          <tr key={file.id}>
                            <th>파일 #{index + 1}</th>
                            <td>
                              <div className="col-sm-10">
                                <span>{file.name}</span>
                                <span className="file-size">( {getConvertedByteSize(file.size)} )</span>
                              </div>
                              <button className="btn_gray" style={{ marginLeft: 10 }} onClick={() => _handleCanceledFileUpload({ fileId: file.id, fileIndex: index })}>
                                취소
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    }
                  </tbody>
                </table>
              </div>
              <div>
                <p className="text-center mt30">
                  <button
                    type="submit"
                    className="btn_green"
                    style={{
                      opacity: isSendable ? 1 : 0.5, // disabled에 해당하는 Style Sheet 적용이 없어서 ...
                      margin: 10,
                    }}
                    // disabled={!isSendable}
                    onClick={_handleSubmitButtonPress}
                  >
                    저장
                  </button>
                  <Link
                    to={searching ? makeUrl('../view', { searching, item, keyword, offset, id }) : makeUrl('../view', { offset, id })}
                    id="btnGroup"
                    className="btn_black btn-history-back"
                    title="취소"
                  >
                    취소
                  </Link>
                </p>
              </div>
            </section>
          </div>
          {/* <!-- .entry-content --> */}
        </article>
      </div>
      {/* <!--//e: #content --> */}
    </div>
  );
};

export default BoardEdit;
